/**
 * Pure floor/stair helpers for the first-person runtime. Multi-floor layouts map
 * a floor index to a world-Y plane and connect floors with axis-aligned stair
 * trigger volumes. These functions are data-driven and game-agnostic.
 */

/** Map of floor index -> world-Y height of that floor's walkable plane. */
export type FloorYMap = Record<number, number>;

export interface StairTrigger {
  fromFloor: number;
  toFloor: number;
  /** XZ centre of the trigger volume. */
  center: [number, number];
  /** XZ size (width, depth) of the trigger volume. */
  size: [number, number];
  /** XZ position the player is placed at on the destination floor. */
  spawnXZ: [number, number];
  /** World-Y of the destination floor. */
  toY: number;
}

/**
 * Returns the floor index whose plane is closest to `worldY`, restricted to the
 * given candidate floors. Used when leaving fly mode to settle onto a floor.
 */
export function nearestFloorLevel(
  worldY: number,
  floorY: FloorYMap,
  candidateFloors: readonly number[],
  fallback: number,
): number {
  let best = fallback;
  let bestDist = Math.abs(worldY - (floorY[fallback] ?? 0));
  for (const floor of candidateFloors) {
    const y = floorY[floor];
    if (y === undefined) continue;
    const dist = Math.abs(worldY - y);
    if (dist < bestDist) {
      best = floor;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Returns the first stair trigger on `fromFloor` whose volume contains the XZ
 * point, or null. Caller is responsible for cooldown/debounce.
 */
export function findStairCrossing(
  x: number,
  z: number,
  fromFloor: number,
  triggers: readonly StairTrigger[],
): StairTrigger | null {
  for (const trigger of triggers) {
    if (trigger.fromFloor !== fromFloor) continue;
    const halfW = trigger.size[0] / 2;
    const halfD = trigger.size[1] / 2;
    if (
      x >= trigger.center[0] - halfW &&
      x <= trigger.center[0] + halfW &&
      z >= trigger.center[1] - halfD &&
      z <= trigger.center[1] + halfD
    ) {
      return trigger;
    }
  }
  return null;
}

// ── Stair runs (walked, not teleported) ──────────────────────────────────────

/** Tolerance on a run's footprint edges, in metres. */
const STAIR_EDGE_EPSILON = 1e-6;

/** A flight the player physically climbs. Mirrors `MapStairRun` in the schema. */
export interface StairRun {
  id: string;
  fromFloor: number;
  toFloor: number;
  axis: 'x' | 'z';
  center: [number, number];
  size: [number, number];
  footAt: -1 | 1;
  /**
   * Tread count. When set (and > 0), `stairRunHeight` quantizes to the flush
   * top of whichever tread a point falls on, instead of interpolating a
   * smooth ramp across the whole run. Omit it for a run with no stepped
   * visual (a genuine ramp/slope) - the smooth interpolation is still
   * correct there.
   */
  steps?: number;
}

/**
 * The run whose footprint contains the point, or null.
 *
 * The footprint is used as authored, with no inset. This is already the exact
 * walkable envelope: `makeWalkable` does not additionally test the walls of
 * the bay a run sits in for a point this function has already matched, since
 * those walls (from a room-authored void, or hand-drawn) sit centred on the
 * run's own edges and re-testing them grown by the player radius would
 * double-bound the same boundary (see `makeWalkable`'s own doc comment).
 * Insetting here instead would leave a band the width of the player radius
 * down each side and across each end where they are on neither the flight
 * nor a floor, which makes the flight impossible to step on to or off at the
 * sides.
 */
export function findStairRun(
  x: number,
  z: number,
  runs: readonly StairRun[],
): StairRun | null {
  for (const run of runs) {
    // A hair of tolerance, for the same reason panels need it: the footprint's
    // edges are reconstructed from a centre and a half-size, so a point landing
    // exactly on the end of a flight can fall a float's width outside it.
    if (
      Math.abs(x - run.center[0]) <= run.size[0] / 2 + STAIR_EDGE_EPSILON &&
      Math.abs(z - run.center[1]) <= run.size[1] / 2 + STAIR_EDGE_EPSILON
    ) {
      return run;
    }
  }
  return null;
}

/**
 * How far up a run a point is: 0 at the foot, 1 at the head. Clamped, so a
 * point at the very edge of the footprint still resolves to a valid height.
 */
export function stairRunProgress(x: number, z: number, run: StairRun): number {
  const along = run.axis === 'x' ? x : z;
  const centre = run.axis === 'x' ? run.center[0] : run.center[1];
  const length = run.axis === 'x' ? run.size[0] : run.size[1];
  if (length <= 0) return 0;
  const low = centre - length / 2;
  // `footAt` -1 means the foot is at the low coordinate, so progress grows with
  // the coordinate; +1 means the flight climbs the other way.
  const fromLow = (along - low) / length;
  const t = run.footAt === -1 ? fromLow : 1 - fromLow;
  return Math.min(1, Math.max(0, t));
}

/**
 * World-Y of the walkable surface of a run at a point.
 *
 * With `steps` set, this is a staircase, not a ramp: the surface a point
 * anywhere along one tread's going actually stands on is that tread's flush
 * top, not a height interpolated continuously across it. A renderer that
 * draws individual treads (e.g. sstk-dwindling's Staircase()) produces
 * exactly that stepped surface - a smooth ramp underneath it would put the
 * player's feet up to a full riser below the tread they visibly stand on,
 * for most of every tread's depth, worsening right after each riser and
 * only agreeing again at each tread's leading nosing.
 */
export function stairRunHeight(
  x: number,
  z: number,
  run: StairRun,
  floorY: FloorYMap,
): number {
  const from = floorY[run.fromFloor] ?? 0;
  const to = floorY[run.toFloor] ?? 0;
  const progress = stairRunProgress(x, z, run);
  if (run.steps && run.steps > 0) {
    const treadIndex = Math.min(run.steps - 1, Math.floor(progress * run.steps));
    const treadTop = (treadIndex + 1) / run.steps;
    return from + (to - from) * treadTop;
  }
  return from + (to - from) * progress;
}

/**
 * Which floor a player standing on a run belongs to.
 *
 * Past the halfway point they are treated as being on the upper floor, so that
 * stepping off the head tests the upper floor's geometry and stepping off the
 * foot tests the lower - without needing a separate transition event.
 */
export function stairRunFloor(x: number, z: number, run: StairRun): number {
  return stairRunProgress(x, z, run) < 0.5 ? run.fromFloor : run.toFloor;
}
